import React, { useState } from "react";
import { useFeatureFlagEnabled } from "posthog-js/react";

import { Button } from "../coreUI/button";
import { ExternalLinkText } from "../coreUI/ExternalLink";
import { Toggle } from "../coreUI/toggle";
import { useAuth } from "../providers/AuthProvider";

export interface AuthFormProps {
  /** Whether to show the mode toggle (login/signup) */
  showModeToggle?: boolean;
  /** Initial auth mode */
  initialMode?: "login" | "signup";
  /** Called when authentication succeeds */
  onAuthSuccess?: () => void;
  /** Called when Google sign-in is initiated */
  onGoogleSignIn?: () => Promise<void>;
  /** Called when login is initiated (for analytics) */
  onLoginInitiated?: () => Promise<void>;
  /** Called when signup is initiated (for analytics) */
  onSignupInitiated?: () => Promise<void>;
  /** Custom callback URL for password signup */
  signupCallbackURL?: string;
  /** Whether to skip navigation after signup (for embedded use) */
  skipNavigationOnSignup?: boolean;
  /** Custom error handling */
  onError?: (error: string) => void;
  /** Custom loading state handling */
  onLoadingChange?: (loading: boolean) => void;
  /** Show verification UI callback */
  onShowEmailVerification?: () => void;
}

export function AuthForm({
  showModeToggle = true,
  initialMode = "signup",
  onAuthSuccess,
  onGoogleSignIn,
  onLoginInitiated,
  onSignupInitiated,
  signupCallbackURL,
  skipNavigationOnSignup = false,
  onError,
  onLoadingChange,
  onShowEmailVerification,
}: AuthFormProps) {
  const [mode, setMode] = useState<"login" | "signup">(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [subscribeToNewsletter, setSubscribeToNewsletter] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const { authenticateWithPassword, signUpWithPassword, authenticate } =
    useAuth();
  const isGoogleAuthEnabled = useFeatureFlagEnabled("web_google_auth_enabled");

  const updateLoading = (newLoading: boolean) => {
    setLoading(newLoading);
    onLoadingChange?.(newLoading);
  };

  const updateError = (newError: string) => {
    setError(newError);
    onError?.(newError);
  };

  const switchMode = (newMode: "login" | "signup") => {
    setMode(newMode);
    setError("");
    // Clear form when switching modes
    setEmail("");
    setPassword("");
    setConfirmPassword("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    updateError("");

    if (mode === "signup") {
      if (password !== confirmPassword) {
        updateError("Passwords do not match");
        return;
      }

      if (password.length < 8) {
        updateError("Password must be at least 8 characters long");
        return;
      }
    }

    updateLoading(true);

    try {
      if (mode === "login") {
        await onLoginInitiated?.();
        const result = await authenticateWithPassword({ email, password });
        if (result?.error) {
          updateError(result.error.message);
        } else {
          onAuthSuccess?.();
        }
      } else {
        await onSignupInitiated?.();
        await signUpWithPassword({
          email,
          password,
          skipNavigation: skipNavigationOnSignup,
          callbackURL: signupCallbackURL,
        });

        if (skipNavigationOnSignup && onShowEmailVerification) {
          // Show email verification UI instead of navigating away
          onShowEmailVerification();
        } else if (!skipNavigationOnSignup) {
          // Let AuthProvider handle navigation to /onboarding/verify-email
          // Don't call onAuthSuccess as navigation is handled by AuthProvider
        } else {
          // skipNavigationOnSignup is true but no onShowEmailVerification callback
          onAuthSuccess?.();
        }
      }
    } catch (err: any) {
      updateError(
        err.message ||
          `An error occurred during ${mode === "login" ? "login" : "signup"}`,
      );
    } finally {
      updateLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (onGoogleSignIn) {
      await onGoogleSignIn();
    } else {
      updateError("");
      updateLoading(true);
      try {
        await authenticate("google");
        onAuthSuccess?.();
      } catch (err: any) {
        updateError(err?.message || "Google sign-in failed. Please try again.");
      } finally {
        updateLoading(false);
      }
    }
  };

  return (
    <div className="flex w-full flex-col items-center justify-center">
      <div className="w-full max-w-md rounded-2xl flex flex-col items-center justify-center">
        {showModeToggle && (
          <div className="w-full flex flex-col items-center mb-6">
            <Toggle
              options={[
                { label: "Sign Up", value: "signup" },
                { label: "Log In", value: "login" },
              ]}
              value={mode}
              onChange={(value) => switchMode(value as "login" | "signup")}
              className="w-full"
            />
          </div>
        )}

        <form
          className="ph-no-capture flex flex-col w-full gap-4"
          onSubmit={handleSubmit}
        >
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="border border-gray-300 rounded-full min-w-[300px] px-4 py-2"
          />

          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="border border-gray-300 rounded-full min-w-[300px] px-4 py-2"
          />

          {mode === "signup" && (
            <input
              type="password"
              placeholder="Confirm password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="border border-gray-300 rounded-full min-w-[300px] px-4 py-2"
            />
          )}

          {mode === "signup" && (
            <label className="flex flex-row gap-2 items-start">
              <input
                type="checkbox"
                checked={subscribeToNewsletter}
                onChange={(e) => setSubscribeToNewsletter(e.target.checked)}
                className="mt-1"
              />
              <span className="text-sm text-foreground-secondary">
                Join our mailing list for the latest research, news about the
                cycle brain connection and more!
              </span>
            </label>
          )}

          {error && (
            <div className="flex flex-row gap-2 items-center text-sm text-red-500 bg-red-100 border-red-500 px-4 py-2 border rounded-full">
              {error}
            </div>
          )}

          <Button
            variant="primary"
            size="default"
            type="submit"
            disabled={loading}
            fullWidth
          >
            {loading
              ? mode === "login"
                ? "Signing in..."
                : "Creating account..."
              : mode === "login"
                ? "Log in"
                : "Create Account"}
          </Button>

          {isGoogleAuthEnabled && (
            <>
              <div className="flex flex-row gap-2 items-center">
                <div className="w-full h-[1px] bg-surface-two"></div>
                <p className="text-sm text-foreground-secondary">or</p>
                <div className="w-full h-[1px] bg-surface-two"></div>
              </div>
              <Button
                variant="google"
                size="default"
                type="button"
                onClick={handleGoogleSignIn}
                disabled={loading}
                fullWidth
              >
                Continue with Google
              </Button>
            </>
          )}
        </form>

        {mode === "signup" && (
          <div className="mt-4 text-sm text-center justify-center text-foreground-secondary inline-flex flex-wrap gap-x-1 gap-y-0">
            By proceeding, you agree to our{" "}
            <ExternalLinkText
              href="https://www.samphireneuro.com/legal/terms"
              text="terms of service"
            />{" "}
            and{" "}
            <ExternalLinkText
              href="https://www.samphireneuro.com/legal/privacy"
              text="privacy policy"
            />
          </div>
        )}
      </div>
    </div>
  );
}
